'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import {
  Wrench,
  ShieldCheck,
  Zap,
  Heart,
  ArrowRight,
  Gift,
  Users,
  Star,
  Copy,
  CheckCheck,
    Clock,
  MapPin,
} from 'lucide-react';

const values = [
  {
    icon: Wrench,
    title: 'Honest Repairs',
    desc: 'No unnecessary upsells. We diagnose the real problem and fix only what needs fixing — then tell you the price upfront.',
  },
  {
    icon: ShieldCheck,
    title: '90-Day Warranty',
    desc: 'Every repair and refurbished device is backed by a 3-month warranty. If something goes wrong, we make it right.',
  },
  {
    icon: Zap,
    title: 'Same-Day Turnaround',
    desc: 'Most repairs are done within a few hours. Mail-in orders are repaired and shipped back the same day we receive them.',
  },
  {
    icon: Heart,
    title: 'Local & Personal',
    desc: "You\'re not a ticket number. You deal directly with Luka — the person who actually fixes your phone.",
  },
];

const loyaltyTiers = [
  {
    tier: 'First Repair',
    reward: 'Welcome discount',
    detail: '$10 off your first repair when you mention this page',
    color: '#8a7a65',
    bg: 'rgba(138,122,101,0.08)',
    border: 'rgba(138,122,101,0.2)',
  },
  {
    tier: 'Refer a Friend',
    reward: '$10 off for you + $10 off for them',
    detail: 'When your referral books a repair, you both save $10',
    color: '#d4a843',
    bg: 'rgba(212,168,67,0.08)',
    border: 'rgba(212,168,67,0.25)',
    highlight: true,
  },
  {
    tier: 'Repeat Customer',
    reward: 'Priority booking',
    detail: 'Return customers get priority scheduling and a free screen clean with every repair',
    color: '#4ade80',
    bg: 'rgba(74,222,128,0.06)',
    border: 'rgba(74,222,128,0.2)',
  },
];

const referralSteps = [
  {
    num: '01',
    title: 'Tell your mate about LP Refurbished',
    desc: 'Share our number or website with anyone who needs a phone repair.',
  },
  {
    num: '02',
    title: 'They mention your name when booking',
    desc: 'Your friend just needs to say your name or number when they call or book online.',
  },
  {
    num: '03',
    title: 'You both get $10 off',
    desc: "Once their repair is complete, you'll each receive $10 off your next repair.",
  },
];

export default function AboutPage() {
  const [copied, setCopied] = useState(false);

  const handleCopyNumber = () => {
    navigator.clipboard?.writeText('0481 446 276');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#0a0a0a' }}>
      <Header />
      <main className="flex-1 pt-16">

        {/* ── Hero ── */}
        <section
          className="border-b py-16 px-6"
          style={{
            background: 'linear-gradient(135deg, #0d0d0d 0%, #111111 60%, #0a0a0a 100%)',
            borderColor: '#2a2a2a',
          }}
        >
          <div className="max-w-[1280px] mx-auto">
            <div className="max-w-2xl">
              <span className="section-label mb-4 inline-block">About LP Refurbished</span>
              <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-5 leading-tight" style={{ color: '#f5f0e8' }}>
                Phone repairs done{' '}
                <span style={{ color: '#d4a843' }}>properly.</span>
              </h1>
              <p className="text-base md:text-lg leading-relaxed mb-8" style={{ color: '#8a7a65' }}>
                LP Refurbished is a solo repair operation run by Luka Pemberton on the Sunshine Coast. No franchise, no middlemen — just fast, honest phone repairs and quality refurbished devices backed by a real warranty.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link href="/repairs" className="btn-primary">
                  Book a Repair
                </Link>
                <a
                  href="tel:0481446276"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-colors"
                  style={{ backgroundColor: '#1a1a1a', color: '#f5f0e8', border: '1px solid #2a2a2a' }}
                >
                  Call 0481 446 276
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ── Story ── */}
        <section className="py-16 px-6 border-b" style={{ borderColor: '#2a2a2a' }}>
          <div className="max-w-[1280px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
              {/* Text — wider */}
              <div className="lg:col-span-3">
                <span className="section-label mb-4 inline-block">The Story</span>
                <h2 className="text-3xl font-extrabold mb-6 tracking-tight" style={{ color: '#f5f0e8' }}>
                  Started from a genuine frustration
                </h2>
                <div className="space-y-4 text-sm leading-relaxed" style={{ color: '#8a7a65' }}>
                  <p>
                    I started LP Refurbished after watching too many people get overcharged or talked into buying a new phone when their existing one just needed a $60 screen replacement. It didn&apos;t sit right with me.
                  </p>
                  <p>
                    I&apos;ve been repairing phones for years — screens, batteries, charging ports, water damage, you name it. What I found is that most repairs are straightforward if you use quality parts and take your time. The problem is most shops rush jobs or cut corners on parts to maximise volume.
                  </p>
                  <p>
                    LP Refurbished is different. Every repair gets my full attention. I source quality parts, I test everything before it leaves my hands, and I back every job with a 90-day warranty. If something isn&apos;t right, you call me directly and I fix it.
                  </p>
                  <p style={{ color: '#c9b99a', fontWeight: 600 }}>
                    — Luka Pemberton, LP Refurbished
                  </p>
                </div>
              </div>

              {/* Stats — narrower */}
              <div className="lg:col-span-2 grid grid-cols-2 gap-4">
                {[
                  { icon: Clock, value: 'Same Day', label: 'Turnaround' },
                  { icon: ShieldCheck, value: '90 Days', label: 'Warranty' },
                  { icon: MapPin, value: 'AUS-Wide', label: 'Mail-In Service' },
                ]?.map((stat) => (
                  <div
                    key={stat?.label}
                    className="rounded-2xl p-5 flex flex-col gap-3"
                    style={{ backgroundColor: '#111111', border: '1px solid #2a2a2a' }}
                  >
                    <stat.icon size={20} style={{ color: '#d4a843' }} />
                    <div>
                      <p className="text-xl font-extrabold" style={{ color: '#f5f0e8' }}>{stat?.value}</p>
                      <p className="text-xs font-semibold mt-0.5" style={{ color: '#5a4a35' }}>{stat?.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Values ── */}
        <section className="py-16 px-6 border-b" style={{ borderColor: '#2a2a2a' }}>
          <div className="max-w-[1280px] mx-auto">
            <div className="mb-10">
              <span className="section-label mb-3 inline-block">What We Stand For</span>
              <h2 className="text-3xl font-extrabold tracking-tight" style={{ color: '#f5f0e8' }}>
                The LP Refurbished difference
              </h2>
            </div>
            {/* Bento-style asymmetric grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {values?.map((v, i) => (
                <div
                  key={v?.title}
                  className={`rounded-2xl p-6 flex flex-col gap-4 ${i === 1 ? 'lg:col-span-2' : ''}`}
                  style={{
                    backgroundColor: i === 1 ? 'rgba(212,168,67,0.06)' : '#111111',
                    border: i === 1 ? '1px solid rgba(212,168,67,0.2)' : '1px solid #2a2a2a',
                  }}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(212,168,67,0.1)' }}
                  >
                    <v.icon size={18} style={{ color: '#d4a843' }} />
                  </div>
                  <div>
                    <h3 className="font-bold text-base mb-2" style={{ color: '#f5f0e8' }}>{v?.title}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: '#8a7a65' }}>{v?.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Referral & Loyalty ── */}
        <section id="referral" className="py-16 px-6 border-b" style={{ borderColor: '#2a2a2a', background: 'linear-gradient(180deg, #0a0a0a 0%, #0f0e0a 100%)' }}>
          <div className="max-w-[1280px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-4 mb-10 items-start">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(212,168,67,0.12)' }}>
                    <Gift size={17} style={{ color: '#d4a843' }} />
                  </div>
                  <span className="section-label">Referral &amp; Loyalty</span>
                </div>
                <h2 className="text-3xl font-extrabold tracking-tight" style={{ color: '#f5f0e8' }}>
                  Rewards for loyal customers
                </h2>
              </div>
              <p className="lg:max-w-sm text-sm leading-relaxed" style={{ color: '#8a7a65' }}>
                LP Refurbished looks after the people who look after us. Refer a mate, come back for another repair, or just mention this page — there&apos;s always something in it for you.
              </p>
            </div>

            {/* Tier cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-14">
              {loyaltyTiers?.map((tier) => (
                <div
                  key={tier?.tier}
                  className="rounded-2xl p-6 flex flex-col gap-3 relative overflow-hidden"
                  style={{ backgroundColor: tier?.bg, border: `1px solid ${tier?.border}` }}
                >
                  {tier?.highlight && (
                    <span
                      className="absolute top-4 right-4 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full"
                      style={{ backgroundColor: 'rgba(212,168,67,0.15)', color: '#d4a843' }}
                    >
                      Most Popular
                    </span>
                  )}
                  <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: tier?.color }}>{tier?.tier}</p>
                  <p className="text-lg font-extrabold leading-snug" style={{ color: '#f5f0e8' }}>{tier?.reward}</p>
                  <p className="text-sm leading-relaxed" style={{ color: '#8a7a65' }}>{tier?.detail}</p>
                </div>
              ))}
            </div>

            {/* How referral works */}
            <div className="rounded-2xl p-8 md:p-10" style={{ backgroundColor: '#111111', border: '1px solid #2a2a2a' }}>
              <div className="flex items-center gap-3 mb-8">
                <Users size={20} style={{ color: '#d4a843' }} />
                <h3 className="text-xl font-extrabold" style={{ color: '#f5f0e8' }}>How the referral works</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {referralSteps?.map((step, i) => (
                  <div key={step?.num} className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl font-extrabold" style={{ color: 'rgba(212,168,67,0.2)' }}>{step?.num}</span>
                      {i < referralSteps?.length - 1 && (
                        <ArrowRight size={14} className="hidden md:block" style={{ color: '#2a2a2a' }} />
                      )}
                    </div>
                    <h4 className="font-bold text-sm" style={{ color: '#f5f0e8' }}>{step?.title}</h4>
                    <p className="text-sm leading-relaxed" style={{ color: '#8a7a65' }}>{step?.desc}</p>
                  </div>
                ))}
              </div>

              {/* Share CTA */}
              <div
                className="flex flex-col sm:flex-row items-start sm:items-center gap-4 rounded-xl p-5"
                style={{ backgroundColor: '#0d0d0d', border: '1px solid #2a2a2a' }}
              >
                <div className="flex-1">
                  <p className="text-sm font-bold mb-0.5" style={{ color: '#f5f0e8' }}>Share our number with your mate</p>
                  <p className="text-xs" style={{ color: '#5a4a35' }}>They mention your name when booking — you both save $10</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-base font-extrabold" style={{ color: '#d4a843' }}>0481 446 276</span>
                  <button
                    onClick={handleCopyNumber}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold transition-all"
                    style={{
                      backgroundColor: copied ? 'rgba(74,222,128,0.1)' : 'rgba(212,168,67,0.1)',
                      color: copied ? '#4ade80' : '#d4a843',
                      border: `1px solid ${copied ? 'rgba(74,222,128,0.2)' : 'rgba(212,168,67,0.2)'}`,
                    }}
                  >
                    {copied ? <CheckCheck size={13} /> : <Copy size={13} />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section
          className="py-14 px-6"
          style={{ background: 'linear-gradient(135deg, #1a1500 0%, #221c00 100%)' }}
        >
          <div className="max-w-[1280px] mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Star size={15} style={{ color: '#d4a843' }} />
                <span className="text-xs font-bold uppercase tracking-widest" style={{ color: '#d4a843' }}>Ready to get started?</span>
              </div>
              <h2 className="text-2xl font-extrabold" style={{ color: '#f5f0e8' }}>Book your repair today</h2>
              <p className="text-sm mt-1" style={{ color: '#8a7a65' }}>Drop-off or mail-in — same-day turnaround, 90-day warranty.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link href="/repairs" className="btn-primary">
                Book a Repair
              </Link>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-colors"
                style={{ backgroundColor: 'rgba(212,168,67,0.08)', color: '#d4a843', border: '1px solid rgba(212,168,67,0.2)' }}
              >
                Get in Touch
              </Link>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}
